import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getDocsFromCache,
  orderBy,
  query,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { RecipeConverter } from '@core/services/suggested-activities/converters/recipe-converter'
import { SuggestedActivityConverter } from '@core/services/suggested-activities/converters/suggested-activity-converter'
import { SuggestedActivitiesErrorConverter } from '@core/services/suggested-activities/converters/suggested-activities-error-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { Recipe, SuggestedActivity } from '@domain/models/suggested-activities'

const SUGGESTED_ACTIVITIES_COLLECTION = 'suggested_activities'
const RECIPES_COLLECTION = 'recipes'

function getCurrentDateKey(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function hashString(input: string): number {
  let hash = 0
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) | 0
  }
  return hash >>> 0
}

function selectDeterministicItemForToday<T extends { uid: string; title: string }>(
  items: T[],
  seedScope: string,
): T | null {
  if (items.length === 0) {
    return null
  }

  const sortedItems = [...items].sort((left, right) => {
    const titleComparison = left.title.localeCompare(right.title)
    if (titleComparison !== 0) {
      return titleComparison
    }
    return left.uid.localeCompare(right.uid)
  })

  const seed = `${getCurrentDateKey()}:${seedScope}`
  const index = hashString(seed) % sortedItems.length
  return sortedItems[index]
}

export class SuggestedActivitiesService {
  private static instance: SuggestedActivitiesService

  private constructor() {}

  public static getInstance(): SuggestedActivitiesService {
    if (!SuggestedActivitiesService.instance) {
      SuggestedActivitiesService.instance = new SuggestedActivitiesService()
    }
    return SuggestedActivitiesService.instance
  }

  private getFirestoreOrThrow(): Firestore {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }
    return firestore
  }

  private getSuggestedActivitiesCollection() {
    return collection(this.getFirestoreOrThrow(), SUGGESTED_ACTIVITIES_COLLECTION)
  }

  private getRecipesCollection() {
    return collection(this.getFirestoreOrThrow(), RECIPES_COLLECTION)
  }

  private async getDocsPreferringCache(
    firestoreQuery: Query<DocumentData>,
  ): Promise<QuerySnapshot<DocumentData>> {
    try {
      const cachedSnapshot = await getDocsFromCache(firestoreQuery)
      if (!cachedSnapshot.empty) {
        return cachedSnapshot
      }
      return getDocs(firestoreQuery)
    } catch {
      return getDocs(firestoreQuery)
    }
  }

  public async fetchSuggestedActivities(): Promise<SuggestedActivity[]> {
    try {
      const firestoreQuery = query(
        this.getSuggestedActivitiesCollection(),
        orderBy('title'),
      )
      const snapshot = await getDocs(firestoreQuery)
      return snapshot.docs.map((item) =>
        SuggestedActivityConverter.fromFirestore(item.id, item.data()),
      )
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async fetchSuggestedActivitiesByCategory(
    categoryId: string,
  ): Promise<SuggestedActivity[]> {
    try {
      const firestoreQuery = query(
        this.getSuggestedActivitiesCollection(),
        where('category_id', '==', categoryId),
      )
      const snapshot = await getDocs(firestoreQuery)
      return snapshot.docs.map((item) =>
        SuggestedActivityConverter.fromFirestore(item.id, item.data()),
      )
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async fetchRecipes(): Promise<Recipe[]> {
    try {
      const firestoreQuery = query(this.getRecipesCollection(), orderBy('title'))
      const snapshot = await getDocs(firestoreQuery)
      return snapshot.docs.map((item) =>
        RecipeConverter.fromFirestore(item.id, item.data()),
      )
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async loadSuggestedActivity(uid: string): Promise<SuggestedActivity | null> {
    try {
      const snapshot = await getDoc(
        doc(this.getFirestoreOrThrow(), SUGGESTED_ACTIVITIES_COLLECTION, uid),
      )

      if (!snapshot.exists()) {
        return null
      }

      return SuggestedActivityConverter.fromFirestore(snapshot.id, snapshot.data())
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async loadRecipe(uid: string): Promise<Recipe | null> {
    try {
      const snapshot = await getDoc(
        doc(this.getFirestoreOrThrow(), RECIPES_COLLECTION, uid),
      )

      if (!snapshot.exists()) {
        return null
      }

      return RecipeConverter.fromFirestore(snapshot.id, snapshot.data())
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async selectSuggestedActivityForToday(
    categoryId: string,
  ): Promise<SuggestedActivity | null> {
    try {
      const firestoreQuery = query(
        this.getSuggestedActivitiesCollection(),
        where('category_id', '==', categoryId),
      )
      const snapshot = await this.getDocsPreferringCache(firestoreQuery)
      const activities = snapshot.docs.map((item) =>
        SuggestedActivityConverter.fromFirestore(item.id, item.data()),
      )
      return selectDeterministicItemForToday(activities, `suggested:${categoryId}`)
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async selectRecipeForToday(): Promise<Recipe | null> {
    try {
      const snapshot = await this.getDocsPreferringCache(
        query(this.getRecipesCollection()),
      )
      const recipes = snapshot.docs.map((item) =>
        RecipeConverter.fromFirestore(item.id, item.data()),
      )
      return selectDeterministicItemForToday(recipes, 'recipes')
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async createSuggestedActivity(
    activity: Omit<SuggestedActivity, 'uid'>,
  ): Promise<string> {
    try {
      const payload = SuggestedActivityConverter.toFirestore(activity)
      const createdDoc = await addDoc(this.getSuggestedActivitiesCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async updateSuggestedActivity(
    uid: string,
    activity: Omit<SuggestedActivity, 'uid'>,
  ): Promise<void> {
    try {
      const payload = SuggestedActivityConverter.toFirestore(activity)
      await updateDoc(
        doc(this.getFirestoreOrThrow(), SUGGESTED_ACTIVITIES_COLLECTION, uid),
        payload,
      )
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async deleteSuggestedActivity(uid: string): Promise<void> {
    try {
      await deleteDoc(
        doc(this.getFirestoreOrThrow(), SUGGESTED_ACTIVITIES_COLLECTION, uid),
      )
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async createRecipe(recipeInput: Omit<Recipe, 'uid'>): Promise<string> {
    try {
      const payload = RecipeConverter.toFirestore(recipeInput)
      const createdDoc = await addDoc(this.getRecipesCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async updateRecipe(uid: string, recipeInput: Omit<Recipe, 'uid'>): Promise<void> {
    try {
      const payload = RecipeConverter.toFirestore(recipeInput)
      await updateDoc(doc(this.getFirestoreOrThrow(), RECIPES_COLLECTION, uid), payload)
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }

  public async deleteRecipe(uid: string): Promise<void> {
    try {
      await deleteDoc(doc(this.getFirestoreOrThrow(), RECIPES_COLLECTION, uid))
    } catch (error) {
      throw SuggestedActivitiesErrorConverter.domainModel(error)
    }
  }
}

const suggestedActivitiesService = SuggestedActivitiesService.getInstance()

export async function fetchSuggestedActivities(): Promise<SuggestedActivity[]> {
  return suggestedActivitiesService.fetchSuggestedActivities()
}

export async function fetchSuggestedActivitiesByCategory(
  categoryId: string,
): Promise<SuggestedActivity[]> {
  return suggestedActivitiesService.fetchSuggestedActivitiesByCategory(categoryId)
}

export async function fetchRecipes(): Promise<Recipe[]> {
  return suggestedActivitiesService.fetchRecipes()
}

export async function loadSuggestedActivity(uid: string): Promise<SuggestedActivity | null> {
  return suggestedActivitiesService.loadSuggestedActivity(uid)
}

export async function loadRecipe(uid: string): Promise<Recipe | null> {
  return suggestedActivitiesService.loadRecipe(uid)
}

export async function selectSuggestedActivityForToday(
  categoryId: string,
): Promise<SuggestedActivity | null> {
  return suggestedActivitiesService.selectSuggestedActivityForToday(categoryId)
}

export async function selectRecipeForToday(): Promise<Recipe | null> {
  return suggestedActivitiesService.selectRecipeForToday()
}

export async function createSuggestedActivity(
  activity: Omit<SuggestedActivity, 'uid'>,
): Promise<string> {
  return suggestedActivitiesService.createSuggestedActivity(activity)
}

export async function updateSuggestedActivity(
  uid: string,
  activity: Omit<SuggestedActivity, 'uid'>,
): Promise<void> {
  return suggestedActivitiesService.updateSuggestedActivity(uid, activity)
}

export async function deleteSuggestedActivity(uid: string): Promise<void> {
  return suggestedActivitiesService.deleteSuggestedActivity(uid)
}

export async function createRecipe(recipeInput: Omit<Recipe, 'uid'>): Promise<string> {
  return suggestedActivitiesService.createRecipe(recipeInput)
}

export async function updateRecipe(
  uid: string,
  recipeInput: Omit<Recipe, 'uid'>,
): Promise<void> {
  return suggestedActivitiesService.updateRecipe(uid, recipeInput)
}

export async function deleteRecipe(uid: string): Promise<void> {
  return suggestedActivitiesService.deleteRecipe(uid)
}
